import cn from 'classnames';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  showSearch,
} from '../../../../../redux/features/dashboard/dashboardSlice';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import { useUpdateRequestMutation } from '../../../../../redux/services/requests/requestsApi';
import { Dialog } from '../../../../ui/Dialog';
import { ButtonTemplate } from '../../../../ui/buttons/ButtonTemplate';
import { AddRequestForm } from '../../../forms/AddRequestForm';
import { EmailForm } from '../../../forms/emailForms/EmailForm';
import { EmailContextProvider } from '../../../../../contexts/EmailContext';
import { RequestActions } from '../RequestActions';
import { RequestBadges } from '../RequestBadges';
import { RequestButtons } from '../RequestButtons';
import { RequestInfoBadges } from '../RequestInfoBadges';
import { RequestNote } from '../RequestNote';

import { useNavigate } from 'react-router';
import { errorMessages } from '../../../../../data/constants';
import { baseFields as emailBaseFields } from '../../../../../lib/mocks/email-mock';
import { getDefaultValuesForEditing } from '../../../../../utils/data';
import { showError } from '../../../../../utils/ui';
import { formatDate } from '../../../../../utils/utils';
import { AddReminderForm } from '../../../forms/AddReminderForm';

export const SearchItemRequests = ({
  client = {},
  request = {},
  isClientsPage = false,
  isMatching = false,
  formProps = {},
  inputName = '',
  setIsClientsOpen = () => {},
  onClientSelect = null,
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);

  const userToken = useSelector(selectUserToken);
  const [editRequest, { error: editingError }] = useUpdateRequestMutation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    id,
    client_id,
    request_type,
    urgency_type,
    status,
    comment,
    note,

    created_at,
    agent_name,
    source,
  } = request;

  const formatedDate = formatDate(created_at || '');
  const hasEmail = Boolean(client?.email?.trim());
  // Assistants see the client but the BE only lets the owner email them
  const canEmailClient = !!client?.can?.update;
  const canSelectForEmail = hasEmail && canEmailClient;
  let selectForEmailLabel = 'Select for email';
  if (!canEmailClient) selectForEmailLabel = 'Client of another agent';
  else if (!hasEmail) selectForEmailLabel = 'Missing email';

  const handleSubmitEditing = async data => {
    const newRequest = await getDefaultValuesForEditing(request);

    const response = await editRequest({
      token: userToken,
      id: request?.id,
      data: { ...newRequest, ...data },
    });

    const errors = editingError || response?.error;

    showError(errors, errorMessages.wentWrong);
  };

  const handleEditRequestClick = () => {
    setIsEditOpen(true);
  };

  const handleEditRequestClose = () => {
    setIsEditOpen(false);
  };

  const handleReminderClick = () => {
    setIsReminderOpen(!isReminderOpen);
  };

  const handleMatchingClick = () => {
    navigate(`/?requestId=${request.id}`);
    dispatch(showSearch({ isSearchVisible: true, searchQuery: '' }));
  };

  const handleSendReplyClick = () => {
    setIsEmailOpen(!isEmailOpen);
  };

  const handleRequestClick = () => {
    if (formProps?.setFieldValue) {
      if (onClientSelect) {
        onClientSelect(client, request);
      } else {
        formProps.setFieldValue(inputName, client_id);
      }
      setIsClientsOpen(false);
    }
  };

  return (
    <div
      className={cn('relative flex flex-col gap-2 py-3 px-2 w-full bg-gray-50 border-t border-gray-200')}
    >
      <div
        className={cn('flex flex-wrap', {
          'flex-col items-start gap-2': isClientsPage,
          'items-center gap-1': !isClientsPage,
        })}
      >
        <RequestButtons
          isClientsPage={isClientsPage}
          isMatching={isMatching}
          id={id}
          formatedDate={formatedDate}
          handleEditRequestClick={handleEditRequestClick}
          handleReminderClick={handleReminderClick}
          handleMatchingClick={handleMatchingClick}
        />

        {isClientsPage && (
          <RequestActions
            request_type={request_type}
            phone={client?.phone}
            email={client?.email}
            client_id={client_id}
            request_id={request?.id}
            canEmail={canEmailClient}
            handleSubmitEditing={handleSubmitEditing}
            handleSendReplyClick={handleSendReplyClick}
          />
        )}

        <div className="flex items-center justify-between w-full gap-2">
          <RequestBadges
            agent_name={agent_name}
            status={status}
            urgency_type={urgency_type}
            handleSubmitEditing={handleSubmitEditing}
          />
          {!isClientsPage && !isMatching && onClientSelect && (
            <button
              type="button"
              onClick={handleRequestClick}
              disabled={!canSelectForEmail}
              className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-purpleColor rounded hover:opacity-90 transition-opacity disabled:bg-gray-300 disabled:hover:opacity-100"
            >
              {selectForEmailLabel}
            </button>
          )}
        </div>
      </div>

      {!isClientsPage && !isMatching && !onClientSelect && (
        <ButtonTemplate
          handleClick={handleRequestClick}
          text="Open the request"
        />
      )}

      {source && source?.length > 0 && (
        <div className="text-xs text-blackColor">
          <span>{source}</span>
        </div>
      )}

      <RequestInfoBadges request={request} />

      <RequestNote
        isClientsPage={isClientsPage}
        handleSubmitEditing={handleSubmitEditing}
        agent_name={agent_name}
        formatedDate={formatedDate}
        comment={comment}
        note={note}
      />

      {isEditOpen && (
        <Dialog
          content={
            <AddRequestForm
              isEdit={true}
              client={client}
              request={request}
              setShowAddRequestForm={setIsEditOpen}
            />
          }
          classes={'w-full sm:max-w-[420px]'}
          isOpen={isEditOpen}
          onClose={handleEditRequestClose}
        />
      )}

      {isReminderOpen && (
        <Dialog
          content={
            <AddReminderForm
              isClientRequest={true}
              setIsAddOpen={setIsReminderOpen}
              idClientRequest={id}
            />
          }
          classes={'w-full sm:max-w-[420px]'}
          isOpen={isReminderOpen}
          onClose={() => setIsReminderOpen(false)}
        />
      )}

      {isEmailOpen && (
        <Dialog
          content={
            <EmailContextProvider
              initialClient={client}
              requestId={request?.id ?? null}
            >
              <EmailForm
                client={client}
                requestId={request?.id ?? null}
                inputs={emailBaseFields}
                handleClose={() => setIsEmailOpen(false)}
              />
            </EmailContextProvider>
          }
          classes={'sm:w-[600px]'}
          isOpen={isEmailOpen}
          onClose={() => setIsEmailOpen(false)}
        />
      )}
    </div>
  );
};
